// src/modules/assetManagement/management/AssetUpload.tsx
// Upload (asset ingestion): drop files, optionally tag and link them, watch progress, retry or escalate failures.
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AlertCircle, CheckCircle2, CloudUpload, Eye, FileAudio, FileImage, FileText, FileVideo,
  Headphones, History, Loader2, Plus, RotateCcw, Tag, Trash2, X,
} from "lucide-react";
import { assetApi } from "../assetApi";
import { SupportModal } from "../components/AssetModals";
import EventProductPicker from "../components/EventProductPicker";
import { eventsForProduct, toggleProduct, uncoveredProducts, type LinkSelection } from "../linkRules";
import {
  goCreateEvent, goCreateProduct, UPLOAD_RETURN,
  type CreatedHandoff, type UploadDoneState, type UploadEntryState,
} from "../createReturn";
import { Button, Chip } from "../components/ui";
import { useAssetQuery } from "../useAssetQuery";
import { ACCEPTED_EXTENSIONS, ENTITY, formatBytes, MAX_ATTEMPTS, ROUTES } from "../utils";
import type { QueueItem, useUploadQueue } from "./useUploadQueue";
import { useUploadDraft } from "./UploadQueueContext";

type Queue = ReturnType<typeof useUploadQueue>;

const KIND_ICON = { image: FileImage, video: FileVideo, audio: FileAudio, document: FileText };

const statusLine = (i: QueueItem) => {
  switch (i.status) {
    case "invalid":
      return { text: i.error ?? "Not accepted", className: "text-red-600" };
    case "ready":
      return { text: "Ready to upload", className: "text-slate-500" };
    case "uploading":
      return !i.started
        ? { text: "Waiting…", className: "text-slate-500" }
        : { text: `Uploading ${Math.round(i.progress)}%${i.attempts > 1 ? ` · attempt ${i.attempts} of ${MAX_ATTEMPTS}` : ""}`, className: "text-slate-600" };
    case "processing":
      return { text: "Storing and processing on the server…", className: "text-blue-600" };
    case "done":
      return { text: "Uploaded", className: "text-emerald-600" };
    case "failed":
      return {
        text: `${i.error} · attempt ${i.attempts} of ${MAX_ATTEMPTS}`,
        className: "text-red-600",
      };
  }
};

const AssetUpload: React.FC<{ queue: Queue; onViewLog: () => void }> = ({ queue, onViewLog }) => {
  const { items, addFiles, start, retry, remove, clearFinished, markTicket } = queue;
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [dragging, setDragging] = useState(false);
  const [tagInput, setTagInput] = useState("");
  // Tags and links live in the shared draft so they survive a trip to Create Event / Create Product
  const { draft, setDraft } = useUploadDraft();
  const { tags, links } = draft;
  const setTags = (next: string[]) => setDraft((d) => ({ ...d, tags: next }));
  const setLinks = (next: LinkSelection) => setDraft((d) => ({ ...d, links: next }));
  const [supportFor, setSupportFor] = useState<QueueItem | null>(null);
  const [created, setCreated] = useState<{ title: string; withoutEventId?: string } | null>(null);
  const { data: events = [], initialLoading: eventsLoading } = useAssetQuery(() => assetApi.listEvents(), []);
  const { data: products = [], initialLoading: productsLoading } = useAssetQuery(() => assetApi.listProducts(), []);

  // Back from Create Event / Create Product: select what was just created
  const handoff = location.state as CreatedHandoff | null;
  useEffect(() => {
    if (!handoff?.createdEventId && !handoff?.createdProductId) return;
    if (eventsLoading || productsLoading) return;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });

    const event = events.find((e) => e.id === handoff.createdEventId);
    if (event) {
      setDraft((d) => {
        // An event made for a product that had none: select that product too
        const withProduct = d.pendingProductId && event.productIds.includes(d.pendingProductId) ? [d.pendingProductId] : [];
        return {
          ...d,
          pendingProductId: undefined,
          links: {
            eventIds: [...new Set([...d.links.eventIds, event.id])],
            productIds: [...new Set([...d.links.productIds, ...withProduct])],
          },
        };
      });
      setCreated({ title: event.title });
      return;
    }

    const product = products.find((p) => p.id === handoff.createdProductId);
    if (!product) return;
    if (eventsForProduct(events, product.id).length === 0) {
      // A product only links to assets through an event that contains it
      setCreated({ title: product.title, withoutEventId: product.id });
      return;
    }
    setDraft((d) =>
      d.links.productIds.includes(product.id) ? d : { ...d, links: toggleProduct(events, d.links, product.id) },
    );
    setCreated({ title: product.title });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoff, eventsLoading, productsLoading]);

  // Opened from another page: remember it so a finished upload goes back there
  const entry = location.state as UploadEntryState | null;
  useEffect(() => {
    if (entry?.uploadReturnTo) setDraft((d) => ({ ...d, returnTo: entry.uploadReturnTo }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.uploadReturnTo]);

  // When every file of the started upload is done, go to Assets (or back to the page that opened Upload).
  // Any failure keeps the user here to retry or get support.
  useEffect(() => {
    const keys = draft.batchKeys;
    if (!keys?.length) return;
    const batch = items.filter((i) => keys.includes(i.key));
    if (batch.length === 0) {
      setDraft((d) => ({ ...d, batchKeys: undefined }));
      return;
    }
    if (batch.some((i) => i.status === "ready" || i.status === "uploading" || i.status === "processing")) return;
    if (batch.some((i) => i.status !== "done")) {
      setDraft((d) => ({ ...d, batchKeys: undefined }));
      return;
    }
    const uploadedAssetIds = batch.map((i) => i.assetId).filter((id): id is string => !!id);
    const returnTo = draft.returnTo;
    setDraft({ tags: [], links: { eventIds: [], productIds: [] } });
    const state: UploadDoneState = { uploadedAssetIds };
    if (returnTo) {
      navigate(returnTo.path, { state });
    } else {
      navigate(ROUTES.management, { state });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, draft.batchKeys]);

  const startUpload = () => {
    setDraft((d) => ({ ...d, batchKeys: ready.map((i) => i.key) }));
    start({ tags, eventIds: links.eventIds, productIds: links.productIds });
  };
  const retryOne = (key: string) => {
    setDraft((d) => ({ ...d, batchKeys: [key] }));
    retry(key);
  };

  const createEventFor = (productId: string) => {
    setDraft((d) => ({ ...d, pendingProductId: productId }));
    goCreateEvent(navigate, UPLOAD_RETURN, [productId]);
  };
  const nothingToLink = !eventsLoading && !productsLoading && events.length === 0 && products.length === 0;

  const ready = items.filter((i) => i.status === "ready");
  const linksIncomplete = uncoveredProducts(events, links).length > 0;
  const counts = {
    done: items.filter((i) => i.status === "done").length,
    failed: items.filter((i) => i.status === "failed" || i.status === "invalid").length,
    active: items.filter((i) => i.status === "uploading" || i.status === "processing").length,
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/^#/, "").replace(/\s+/g, "-");
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput("");
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const acceptAttr = Object.keys(ACCEPTED_EXTENSIONS).map((x) => `.${x}`).join(",");

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <div className="xl:col-span-2 space-y-4 min-w-0">
        {/* Drop zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
          className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition ${
            dragging ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50"
          }`}
        >
          <CloudUpload size={36} className={`mx-auto ${dragging ? "text-emerald-600" : "text-slate-400"}`} />
          <p className="mt-3 font-medium text-slate-800">Drag files here, or click to browse</p>
          <p className="text-sm text-slate-500 mt-1">Images, video, audio and documents · up to 10 GB per file</p>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={acceptAttr}
            className="hidden"
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        {/* Queue */}
        {items.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-b border-slate-100">
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">{items.length} file{items.length === 1 ? "" : "s"}</span>
                {counts.active > 0 && ` · ${counts.active} in progress`}
                {counts.done > 0 && ` · ${counts.done} uploaded`}
                {counts.failed > 0 && <span className="text-red-600"> · {counts.failed} failed</span>}
              </p>
              <div className="flex gap-2">
                {counts.failed > 0 && (
                  <Button size="sm" variant="ghost" icon={<History size={14} />} onClick={onViewLog}>
                    Upload log
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={clearFinished}>
                  Clear finished
                </Button>
              </div>
            </div>
            <ul className="divide-y divide-slate-100">
              {items.map((i) => {
                const Icon = KIND_ICON[i.kind];
                const line = statusLine(i);
                const exhausted = i.status === "failed" && i.attempts >= MAX_ATTEMPTS;
                return (
                  <li key={i.key} className="px-5 py-3 flex items-center gap-4">
                    <span
                      className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                        i.status === "failed" || i.status === "invalid"
                          ? "bg-red-50 text-red-500"
                          : i.status === "done"
                            ? "bg-emerald-50 text-emerald-600"
                            : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {i.status === "done" ? (
                        <CheckCircle2 size={18} />
                      ) : i.status === "failed" || i.status === "invalid" ? (
                        <AlertCircle size={18} />
                      ) : i.status === "processing" ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Icon size={18} />
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-sm font-medium text-slate-800 truncate">{i.file.name}</p>
                        <span className="text-xs text-slate-400 shrink-0">{formatBytes(i.file.size)}</span>
                      </div>
                      <p className={`text-xs mt-0.5 truncate ${line.className}`}>
                        {line.text}
                        {i.ticketRef && <span className="text-slate-500"> · ticket {i.ticketRef}</span>}
                      </p>
                      {(i.status === "uploading" || i.status === "processing") && (
                        <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${i.status === "processing" ? "bg-blue-500 animate-pulse" : "bg-emerald-500"}`}
                            style={{ width: `${i.progress}%` }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {i.status === "failed" && !exhausted && (
                        <Button size="sm" icon={<RotateCcw size={13} />} onClick={() => retryOne(i.key)}>
                          Retry
                        </Button>
                      )}
                      {(exhausted || i.status === "invalid") && !i.ticketRef && (
                        <Button size="sm" icon={<Headphones size={13} />} onClick={() => setSupportFor(i)}>
                          Get support
                        </Button>
                      )}
                      {(i.status === "done" || i.status === "failed") && i.assetId && (
                        <Link to={ROUTES.managedAsset(i.assetId)} state={{ trail: [{ label: "Asset Management", to: `${location.pathname}${location.search}` }] }}>
                          <Button size="sm" variant="ghost" icon={<Eye size={13} />}>
                            View
                          </Button>
                        </Link>
                      )}
                      {(i.status === "ready" || i.status === "invalid" || i.status === "failed" || i.status === "done") && (
                        <button
                          type="button"
                          onClick={() => remove(i.key)}
                          className="p-1.5 text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-100"
                          aria-label={`Remove ${i.file.name}`}
                          title="Remove from list"
                        >
                          {i.status === "done" ? <X size={15} /> : <Trash2 size={15} />}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* Options + start */}
      <aside className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Apply to this upload</h3>
            <p className="text-xs text-slate-500 mt-0.5">Optional. You can also tag and link later.</p>
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
              <Tag size={13} /> Tags
            </label>
            <div className="flex gap-2">
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="Type a tag and press Enter"
                className="flex-1 min-w-0 px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
              <Button size="sm" onClick={addTag} disabled={!tagInput.trim()}>Add</Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tags.map((t) => (
                  <Chip key={t} className={ENTITY.asset.chip} onRemove={() => setTags(tags.filter((x) => x !== t))}>
                    #{t}
                  </Chip>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Link to</p>
              {!nothingToLink && (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" icon={<Plus size={13} />} title="Create an event and come back here"
                    onClick={() => goCreateEvent(navigate, UPLOAD_RETURN)}>
                    Event
                  </Button>
                  <Button size="sm" variant="ghost" icon={<Plus size={13} />} title="Create a product and come back here"
                    onClick={() => goCreateProduct(navigate, UPLOAD_RETURN)}>
                    Product
                  </Button>
                </div>
              )}
            </div>
            {nothingToLink ? (
              <div className="rounded-lg border border-dashed border-slate-300 p-4 text-center">
                <p className="text-sm text-slate-700">No events or products yet</p>
                <p className="text-xs text-slate-500 mt-1">Create one and you'll come back here with it selected. Your files stay in the list.</p>
                <div className="flex justify-center gap-2 mt-3">
                  <Button size="sm" icon={<Plus size={13} />} onClick={() => goCreateEvent(navigate, UPLOAD_RETURN)}>Create event</Button>
                  <Button size="sm" icon={<Plus size={13} />} onClick={() => goCreateProduct(navigate, UPLOAD_RETURN)}>Create product</Button>
                </div>
              </div>
            ) : (
              <EventProductPicker events={events} products={products} value={links} onChange={setLinks} layout="stacked" />
            )}
            {created && (
              created.withoutEventId ? (
                <div className="mt-2 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <span className="font-medium">{created.title}</span> was created but isn't in an event yet. Assets link to a
                    product through one of its events.
                    <button type="button" className="block mt-1 font-semibold underline" onClick={() => createEventFor(created.withoutEventId!)}>
                      Create an event for {created.title}
                    </button>
                  </div>
                  <button type="button" aria-label="Dismiss" onClick={() => setCreated(null)}><X size={13} /></button>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  <CheckCircle2 size={14} className="shrink-0" />
                  <span className="flex-1"><span className="font-medium">{created.title}</span> created and selected</span>
                  <button type="button" aria-label="Dismiss" onClick={() => setCreated(null)}><X size={13} /></button>
                </div>
              )
            )}
          </div>

          <Button
            variant="primary"
            className="w-full"
            icon={<CloudUpload size={16} />}
            disabled={ready.length === 0 || linksIncomplete}
            title={linksIncomplete ? "Choose an event for each selected product" : undefined}
            onClick={startUpload}
          >
            {ready.length > 0 ? `Upload ${ready.length} file${ready.length === 1 ? "" : "s"}` : "Add files to upload"}
          </Button>
        </div>
      </aside>

      {supportFor && (
        <SupportModal
          fileName={supportFor.file.name}
          defaultCategory="Upload failed"
          defaultDescription={`Upload of ${supportFor.file.name} (${formatBytes(supportFor.file.size)}) failed ${supportFor.attempts} time${
            supportFor.attempts === 1 ? "" : "s"
          }.\nLast error: ${supportFor.errorCode} – ${supportFor.error}\nStage: ${supportFor.stage}\n\n`}
          onClose={() => setSupportFor(null)}
          onRaised={(t) => {
            markTicket(supportFor.key, t.ref);
            void assetApi.resolveFileAttempts(supportFor.key, "ticket_raised", t.ref);
          }}
        />
      )}
    </div>
  );
};

export default AssetUpload;